import { useMutation } from '@tanstack/react-query';
import Layout from 'components/Layout';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import twitterService from '../services/twitterApi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { connectSocialPlatform } from 'store/slices/authSlice';
import { RootState } from 'store';

export default function TwitterAuthState() {
  const [param] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const isError = param.get('error');
  const isSuccess = param.get('twitter_access_token');

  const completeXAuth = useMutation({
    mutationKey: ['complete-x-auth'],
    mutationFn: twitterService.completeAuth,
    onSuccess: (response) => {
      // Get token from URL params and user_id from response
      const twitterToken = param.get('twitter_access_token');
      const userId = response.data.twitter_user_id;

      if (twitterToken && userId) {
        // Update Redux state with Twitter connection
        dispatch(
          connectSocialPlatform({
            platform: 'x',
            token: twitterToken,
            username: userId,
            expiresAt: Date.now() + 60 * 24 * 60 * 60 * 1000,
          })
        );
        setShowModal(true);
      }
    },
    onError: () => {
      setShowModal(true);
    },
  });

  useEffect(() => {
    if (isSuccess && !completeXAuth.isSuccess) {
      completeXAuth.mutate({
        twitter_access_token: param.get('twitter_access_token') || '',
        twitter_refresh_token: param.get('twitter_refresh_token') || '',
      });
    }
  }, [isSuccess]);

  const handleBackToPost = () => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: 'TWITTER_AUTH_SUCCESS',
          data: {
            twitter_access_token: param.get('twitter_access_token'),
            twitter_social_user_id: completeXAuth.data?.data.twitter_user_id,
          },
        },
        '*'
      );
    }
    window.close();
  };

  const handleRetryConnect = () => {
    twitterService.getAuthUrl().then(({ data }) => {
      window.open(data.url, '_blank');
    });
  };

  // Only show success dialog if mutation was successful
  if (completeXAuth.isSuccess) {
    return (
      <Layout>
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="flex flex-col items-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
              <DialogTitle className="text-xl font-semibold text-center">
                X Account Connected Successfully
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                Your X account has been successfully connected. You can now publish
                and schedule posts directly to X.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                className="text-sm"
                onClick={handleBackToPost}
              >
                Back to Generated Post
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }
  console.log(completeXAuth.data);
  console.log(completeXAuth.isError);
  console.log(isError);

  // Only show error dialog if there's an error param or mutation failed
  if (isError || completeXAuth.isError) {
    return (
      <Layout>
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="flex flex-col items-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <DialogTitle className="text-xl font-semibold text-center">
                Failed to Connect X Account
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                There was an error connecting your X account. Please try again or
                contact support if the issue persists.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center gap-4 pt-4">
              <Button
                variant="customSecondary"
                onClick={handleBackToPost}
                className="text-sm"
              >
                Back to Generated Post
              </Button>
              <Button
                variant="outline"
                className="text-sm"
                onClick={handleRetryConnect}
              >
                Connect X Account
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // Loading state
  return (
    <Layout>
      <div className="flex justify-center items-center h-full">
        <div className="animate-pulse">Connecting to X...</div>
      </div>
    </Layout>
  );
}
